// src\hooks\useUserProfile.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { 
  fetchUserById, 
  updateUserLocation,
  uploadUserProfilePicture, 
  updateProfileData, 
  fetchProfileByPublicId 
} from '../store/services/userService';
import { useUserStore } from '../store/userStore';

export const useUserProfile = () => {
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const setCurrUserActive = useUserStore((state) => state.setCurrUserActive);

  const queryResult = useQuery({
    queryKey: ['userProfile', user?.id],
    queryFn: async () => {
      // 1. Fetch the profile from Supabase
      const data = await fetchUserById(user.id);

      // 2. AUTO-PATCH LOGIC: 
      // If profile has no location data, patch with the complete object stored in LocalStorage
if (data) {
  let userLoc = data.location;
  if (typeof userLoc === 'string') {
    try { userLoc = JSON.parse(userLoc); } catch (e) {}
  }

  // If no quarter/address exists in profile, sync from LocalStorage
  if (!userLoc || (!userLoc.quarter && !userLoc.address)) {
    const storedLocation = localStorage.getItem('app-user-location');
    if (storedLocation) {
      try {
        const parsedLocation = JSON.parse(storedLocation);
        if (parsedLocation && (parsedLocation.quarter || parsedLocation.address)) {
          const updatedData = await updateUserLocation(user.id, parsedLocation);
          setCurrUserActive(updatedData);
          return updatedData;
        }
      } catch (e) {
        console.error("Failed to parse local location during auto-patch:", e);
      }
    }
  }
}

      // 3. Update Zustand and return the data as usual
      setCurrUserActive(data);
      return data;
    },
    enabled: !authLoading && !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const updateLocationMutation = useMutation({
    mutationFn: ({ userId, locationData }) => updateUserLocation(userId, locationData),
    onSuccess: (updatedData) => {
      queryClient.setQueryData(['userProfile', user?.id], updatedData);
      setCurrUserActive(updatedData);
    },
    onError: (err) => {
      console.error("Mutation Error:", err);
    }
  });

  return {
    ...queryResult,
    updateLocation: updateLocationMutation.mutateAsync,
    isUpdating: updateLocationMutation.isLoading,
    supabaseAuthUser: user,
    isAuthLoading: authLoading,
  };
};

export const useUpdateProfilePicture = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, userId }) => {
      return await uploadUserProfilePicture(file, userId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['profile', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProfileData,
    onSuccess: (updatedData) => {
      queryClient.setQueryData(['userProfile', updatedData.id], updatedData);
      queryClient.invalidateQueries({ queryKey: ['publicProfile', updatedData.public_id] });
      toast.success('Profile updated successfully!');
    },
    onError: (error) => {
      toast.error(`Update failed: ${error.message}`);
    },
  });
};

export const usePublicProfile = (publicId) => {
  return useQuery({
    queryKey: ['publicProfile', publicId],
    queryFn: () => fetchProfileByPublicId(publicId),
    enabled: !!publicId,
    staleTime: 1000 * 60 * 5,
  });
};